import React, { useState, useEffect, useRef, useId } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../utils/dateFormat';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { StatusChip } from '../ds/components/StatusChip';
import { InputField } from '../ds/InputField';
import { ModalShell } from '../ds/ModalShell';
import { ConfirmModal } from '../ds/ConfirmModal';
import { DevicesPanel } from './DevicesPanel';
import CeebFlagsPanel from './CeebFlagsPanel';

import { API_BASE_URL } from '../services/api';

interface CompanySettings {
  companyName: string;
  nip: string;
  address: string;
  phone: string;
  email: string;
  logoUrl?: string;
  announcementTemplate?: string;
  // Mistrz kominiarski na protokołach CEEB (migr. 042) — bot odmawia startu bez wartości.
  protocolMasterName?: string;
  // 060: osoby uczestniczące per miejsce CEEB (puste = mistrz) + rubryka zaleceń lokali
  // (062: trzeci tryb 'other_deficiencies').
  ceebParticipantBuilding?: string;
  ceebParticipantControl?: string;
  ceebParticipantApartment?: string;
  ceebApartmentRecsPlacement?: RecsPlacement;
}

// 060/062: tryby rubryki zaleceń lokali — lustro PLACEMENT_MODES z backend/src/utils/apartmentRecsPlacement.js.
type RecsPlacement = 'abnormalities' | 'by_card' | 'other_deficiencies';
const RECS_PLACEMENTS: readonly RecsPlacement[] = ['abnormalities', 'by_card', 'other_deficiencies'];
const toRecsPlacement = (v: unknown): RecsPlacement =>
  (RECS_PLACEMENTS as readonly unknown[]).includes(v) ? (v as RecsPlacement) : 'abnormalities';

interface Employee {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  role: 'admin' | 'sweep';
}

/* ---------- DS visual helpers (no logic, presentation only) ---------- */

// Flat DS card (replaces MUI Paper).
const card: React.CSSProperties = {
  background: 'var(--surface-card)',
  border: 'var(--border-width) solid var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-6)',
};

// Section heading inside a card.
const sectionHeading: React.CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-h2)',
  fontWeight: 'var(--weight-bold)',
  lineHeight: 'var(--leading-tight)',
  color: 'var(--text-heading)',
};

const hairline: React.CSSProperties = {
  border: 'none',
  borderTop: 'var(--border-width) solid var(--border)',
  margin: 'var(--space-4) 0',
};

// DS status banner (replaces MUI Alert). severity drives the left bar + tint.
function Banner({
  severity,
  children,
  onClose,
}: {
  severity: 'success' | 'error' | 'info';
  children: React.ReactNode;
  onClose?: () => void;
}) {
  const meta = {
    success: { bg: 'var(--status-ok-bg)', bar: 'var(--status-ok-bar)', color: 'var(--status-ok)', icon: 'check-circle' as const },
    error: { bg: 'var(--status-deny-bg)', bar: 'var(--status-deny-bar)', color: 'var(--status-deny)', icon: 'triangle-alert' as const },
    info: { bg: 'var(--blue-100)', bar: 'var(--action-primary)', color: 'var(--action-primary)', icon: 'help' as const },
  }[severity];

  return (
    <div
      role={severity === 'error' ? 'alert' : 'status'}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--space-3)',
        padding: 'var(--space-4)',
        marginBottom: 'var(--space-5)',
        background: meta.bg,
        borderLeft: `6px solid ${meta.bar}`,
        borderRadius: 'var(--radius-md)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-body)',
        lineHeight: 'var(--leading-normal)',
        color: 'var(--text-primary)',
      }}
    >
      <span style={{ color: meta.color, flex: 'none', marginTop: 2 }}>
        <Icon name={meta.icon} size={22} />
      </span>
      <div style={{ flex: 1 }}>{children}</div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Zamknij"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            flex: 'none',
            padding: 0,
            background: 'transparent',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
          }}
        >
          <Icon name="x" size={18} />
        </button>
      )}
    </div>
  );
}

// Minimal DS spinner (replaces MUI CircularProgress) — rotating refresh icon.
function Spinner({ size = 36 }: { size?: number }) {
  return (
    <span
      aria-label="Ładowanie"
      role="status"
      style={{
        display: 'inline-flex',
        color: 'var(--action-primary)',
        animation: 'ds-spin 0.9s linear infinite',
      }}
    >
      <Icon name="refresh" size={size} />
    </span>
  );
}

// Native select styled with DS tokens (role dropdown).
function SelectField({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  children: React.ReactNode;
}) {
  const id = useId();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <label
        htmlFor={id}
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-label)',
          fontWeight: 'var(--weight-semibold)',
          lineHeight: 'var(--leading-snug)',
          color: 'var(--text-secondary)',
        }}
      >
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={onChange}
        style={{
          width: '100%',
          minHeight: 'var(--touch-min)',
          padding: '0 var(--space-4)',
          boxSizing: 'border-box',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-body)',
          color: 'var(--text-primary)',
          background: 'var(--surface-card)',
          border: 'var(--border-width-strong) solid var(--border-strong)',
          borderRadius: 'var(--radius-md)',
          outline: 'none',
          cursor: 'pointer',
        }}
      >
        {children}
      </select>
    </div>
  );
}

const SettingsTab: React.FC = () => {
  const { token, user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [settings, setSettings] = useState<CompanySettings>({
    companyName: '',
    nip: '',
    address: '',
    phone: '',
    email: '',
    announcementTemplate: '',
  });
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Employee dialog state
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [employeeForm, setEmployeeForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    role: 'sweep' as 'admin' | 'sweep',
  });

  // Employee delete confirmation state (replaces window.confirm)
  const [deleteEmployeeId, setDeleteEmployeeId] = useState<string | null>(null);

  // CEEB integration state
  const [ceebLogin, setCeebLogin] = useState('');
  const [ceebPassword, setCeebPassword] = useState('');
  const [ceebConnectionStatus, setCeebConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected');
  const [ceebLastConnected, setCeebLastConnected] = useState<string | null>(null);

  // Hidden file input ref for logo upload (native input + DS-styled trigger)
  const logoInputRef = useRef<HTMLInputElement>(null);

  // B1 (test akceptacyjny 2026-08-05): SUPERADMIN nie ma `company_id` (stoi ponad firmami),
  // a cała zakładka wisiała na `user.companyId` — KAŻDA operacja kończyła się cichym
  // `return`. Właściciel widział puste dane firmy, „Brak pracowników" (nieprawda) i brak
  // statusu CEEB, a „Zapisz zmiany" nic nie robiło — to blokowało krok go-live
  // „Ustawienia → Pracownicy → konto kominiarza". Superadmin wybiera więc firmę:
  // przy jednej (pilotaż) wchodzi automatycznie, przy wielu dostaje selektor.
  const [companies, setCompanies] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const isSuperadmin = user?.role === 'superadmin';
  const companyId = user?.companyId || selectedCompanyId;

  useEffect(() => {
    if (!token || !isSuperadmin || user?.companyId) return;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/companies`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) throw new Error('lista firm niedostępna');
        const list = await r.json();
        setCompanies(list.map((c: any) => ({ id: c.id, name: c.name })));
        setSelectedCompanyId((prev) => prev || (list[0] ? list[0].id : null));
      } catch (e) {
        console.error('Error loading companies (superadmin):', e);
        setErrorMessage('Nie udało się pobrać listy firm — zaloguj się jako administrator firmy.');
      }
    })();
  }, [token, isSuperadmin, user?.companyId]);

  // Load company settings and employees
  useEffect(() => {
    if (!companyId) return; // superadmin czeka na ustalenie firmy
    loadSettings();
    loadEmployees();
    loadCeebStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const loadSettings = async () => {
    if (!token || !companyId) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/companies/${companyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSettings({
          companyName: data.name || '',
          nip: data.nip || '',
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          logoUrl: data.logoUrl,
          announcementTemplate: data.announcementTemplate || 'Szanowni Państwo,\n\nInformujemy, że w dniu {data} odbędzie się przegląd kominowy w budynku przy {adres}.\n\nKominiarz: {kominiarz}\nTelefon: {telefon}\n\nProsimy o zapewnienie dostępu do wszystkich przewodów kominowych.\n\nZ poważaniem,\n{firma}',
          protocolMasterName: data.protocolMasterName || '',
          ceebParticipantBuilding: data.ceebParticipantBuilding || '',
          ceebParticipantControl: data.ceebParticipantControl || '',
          ceebParticipantApartment: data.ceebParticipantApartment || '',
          ceebApartmentRecsPlacement: toRecsPlacement(data.ceebApartmentRecsPlacement),
        });
        if (data.logoUrl) {
          setLogoPreview(data.logoUrl);
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setErrorMessage('Błąd wczytywania ustawień');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    if (!token || !companyId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/companies/${companyId}/employees`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setErrorMessage('Proszę wybrać plik graficzny');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrorMessage('Plik jest za duży. Maksymalny rozmiar to 5MB');
        return;
      }

      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setErrorMessage('');
    }
  };

  const handleSaveCompanySettings = async () => {
    if (!token || !companyId) return;

    setSaveLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const formData = new FormData();
      formData.append('name', settings.companyName);
      formData.append('nip', settings.nip);
      formData.append('address', settings.address);
      formData.append('phone', settings.phone);
      formData.append('email', settings.email);
      formData.append('announcementTemplate', settings.announcementTemplate || '');
      // Puste pole POMIJAMY (backend też by je pominął): bot ma twardy guard na brak
      // nazwiska mistrza — nie da się go stąd wyczyścić, tylko zmienić.
      if ((settings.protocolMasterName || '').trim()) {
        formData.append('protocolMasterName', settings.protocolMasterName!.trim());
      }
      // 060: puste pole osoby = rola wraca do mistrza kominiarskiego (backend zapisuje NULL).
      formData.append('ceebParticipantBuilding', (settings.ceebParticipantBuilding || '').trim());
      formData.append('ceebParticipantControl', (settings.ceebParticipantControl || '').trim());
      formData.append('ceebParticipantApartment', (settings.ceebParticipantApartment || '').trim());
      formData.append('ceebApartmentRecsPlacement', settings.ceebApartmentRecsPlacement || 'abnormalities');

      if (logoFile) {
        formData.append('logo', logoFile);
      }

      const response = await fetch(`${API_BASE_URL}/companies/${companyId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setSuccessMessage('Ustawienia zapisane pomyślnie');
        if (data.logoUrl) {
          setSettings({ ...settings, logoUrl: data.logoUrl });
          setLogoPreview(data.logoUrl);
        }
        setLogoFile(null);
      } else {
        const error = await response.json();
        setErrorMessage(error.error || 'Błąd podczas zapisywania ustawień');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setErrorMessage('Błąd podczas zapisywania ustawień');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleAddEmployee = () => {
    setEditingEmployee(null);
    setEmployeeForm({
      fullName: '',
      email: '',
      phone: '',
      password: '',
      role: 'sweep',
    });
    setEmployeeDialogOpen(true);
  };

  const handleEditEmployee = (employee: Employee) => {
    setEditingEmployee(employee);
    setEmployeeForm({
      fullName: employee.fullName,
      email: employee.email,
      phone: employee.phone,
      password: '',
      role: employee.role,
    });
    setEmployeeDialogOpen(true);
  };

  const handleSaveEmployee = async () => {
    if (!token || !companyId) return;

    setSaveLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const url = editingEmployee
        ? `${API_BASE_URL}/users/${editingEmployee.id}`
        : `${API_BASE_URL}/companies/${companyId}/employees`;

      const method = editingEmployee ? 'PUT' : 'POST';

      const body: any = {
        fullName: employeeForm.fullName,
        email: employeeForm.email,
        phone: employeeForm.phone,
        role: employeeForm.role,
        companyId, // B1: superadmin zakłada konto w WYBRANEJ firmie, nie we własnej (nie ma)
      };

      if (employeeForm.password) {
        body.password = employeeForm.password;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        setSuccessMessage(editingEmployee ? 'Pracownik zaktualizowany' : 'Pracownik dodany');
        setEmployeeDialogOpen(false);
        loadEmployees();
      } else {
        const error = await response.json();
        setErrorMessage(error.error || 'Błąd podczas zapisywania pracownika');
      }
    } catch (error) {
      console.error('Error saving employee:', error);
      setErrorMessage('Błąd podczas zapisywania pracownika');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteEmployee = async (employeeId: string) => {
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/users/${employeeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setSuccessMessage('Pracownik usunięty');
        loadEmployees();
      } else {
        const error = await response.json();
        setErrorMessage(error.error || 'Błąd podczas usuwania pracownika');
      }
    } catch (error) {
      console.error('Error deleting employee:', error);
      setErrorMessage('Błąd podczas usuwania pracownika');
    }
  };

  // CEEB functions
  const loadCeebStatus = async () => {
    if (!token || !companyId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/companies/${companyId}/ceeb-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCeebConnectionStatus(data.connected ? 'connected' : 'disconnected');
        setCeebLastConnected(data.lastConnected);
      }
    } catch (error) {
      console.error('Error loading CEEB status:', error);
    }
  };

  const handleCeebConnect = async () => {
    if (!companyId) {
      setErrorMessage('Brak informacji o firmie');
      return;
    }

    if (!ceebLogin || !ceebPassword) {
      setErrorMessage('Wprowadź login i hasło CEEB');
      return;
    }

    setCeebConnectionStatus('connecting');
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/companies/${companyId}/ceeb-connect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ceebLogin,
          ceebPassword,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCeebConnectionStatus('connected');
        setCeebLastConnected(new Date().toISOString());
        setSuccessMessage('Połączono z CEEB pomyślnie');
        setCeebPassword(''); // Clear password from state
      } else {
        const error = await response.json();
        setCeebConnectionStatus('error');
        setErrorMessage(error.error || 'Błąd połączenia z CEEB');
      }
    } catch (error) {
      console.error('Error connecting to CEEB:', error);
      setCeebConnectionStatus('error');
      setErrorMessage('Błąd połączenia z CEEB');
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <Spinner size={44} />
      </div>
    );
  }

  const tabs = ['Dane firmy', 'Pracownicy', 'Szablon ogłoszeń', 'Integracja CEEB', 'Tablety'];

  return (
    <div style={{ fontFamily: 'var(--font-sans)' }}>
      {/* Keyframes for the DS spinner */}
      <style>{`@keyframes ds-spin { to { transform: rotate(360deg); } }`}</style>

      <h1
        style={{
          margin: '0 0 var(--space-5) 0',
          fontSize: 'var(--text-h1)',
          fontWeight: 'var(--weight-bold)',
          lineHeight: 'var(--leading-tight)',
          color: 'var(--text-heading)',
        }}
      >
        Ustawienia
      </h1>

      {successMessage && (
        <Banner severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Banner>
      )}

      {errorMessage && (
        <Banner severity="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Banner>
      )}

      {/* Tabs — simple underline (2px --action-primary) */}
      <div
        role="tablist"
        aria-label="ustawienia"
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          borderBottom: 'var(--border-width) solid var(--border)',
          marginBottom: 'var(--space-6)',
          flexWrap: 'wrap',
        }}
      >
        {tabs.map((label, index) => {
          const active = tabValue === index;
          return (
            <button
              key={label}
              role="tab"
              type="button"
              aria-selected={active}
              id={`settings-tab-${index}`}
              aria-controls={`settings-tabpanel-${index}`}
              onClick={() => setTabValue(index)}
              style={{
                minHeight: 'var(--touch-min)',
                padding: '0 var(--space-4)',
                background: 'transparent',
                border: 'none',
                borderBottom: `2px solid ${active ? 'var(--action-primary)' : 'transparent'}`,
                marginBottom: -1,
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-label)',
                fontWeight: active ? 'var(--weight-semibold)' : 'var(--weight-medium)',
                color: active ? 'var(--action-primary)' : 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Tab 1: Company Settings */}
      {tabValue === 0 && (
        <div role="tabpanel" id="settings-tabpanel-0" aria-labelledby="settings-tab-0">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(260px, 1fr) minmax(0, 2fr)',
              gap: 'var(--space-5)',
              alignItems: 'start',
            }}
          >
            {/* Logo Section */}
            <div style={card}>
              <h2 style={sectionHeading}>Logo firmy</h2>
              <hr style={hairline} />

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 'var(--space-4)',
                }}
              >
                <div
                  style={{
                    width: 200,
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--white)',
                    border: 'var(--border-width) solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    overflow: 'hidden',
                    color: 'var(--text-muted)',
                  }}
                >
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo firmy"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <Icon name="building" size={100} />
                  )}
                </div>

                <Button
                  variant="secondary"
                  icon="upload"
                  fullWidth
                  onClick={() => logoInputRef.current?.click()}
                >
                  Wybierz logo
                </Button>
                <input
                  ref={logoInputRef}
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleLogoChange}
                />

                <p
                  style={{
                    margin: 0,
                    textAlign: 'center',
                    fontSize: 'var(--text-caption)',
                    lineHeight: 'var(--leading-snug)',
                    color: 'var(--text-muted)',
                  }}
                >
                  Dozwolone formaty: JPG, PNG, GIF
                  <br />
                  Maksymalny rozmiar: 5MB
                </p>
              </div>
            </div>

            {/* B1: superadmin stoi ponad firmami — przy więcej niż jednej musi wskazać,
                którą konfiguruje. Przy jednej (pilotaż) wchodzi w nią automatycznie
                i selektor się nie pokazuje. */}
            {isSuperadmin && !user?.companyId && companies.length > 1 && (
              <div style={{ ...card, marginBottom: 'var(--space-5)' }}>
                <SelectField
                  label="Konfigurowana firma (widok superadmina)"
                  value={selectedCompanyId || ''}
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                >
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </SelectField>
              </div>
            )}

            {/* Company Information Section */}
            <div style={card}>
              <h2 style={sectionHeading}>Dane firmy</h2>
              <hr style={hairline} />

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <InputField
                  label="Nazwa firmy"
                  value={settings.companyName}
                  onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
                  required
                />

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: 'var(--space-4)',
                  }}
                >
                  <InputField
                    label="NIP"
                    value={settings.nip}
                    onChange={(e) => setSettings({ ...settings, nip: e.target.value })}
                    required
                  />
                  <InputField
                    label="Telefon"
                    value={settings.phone}
                    onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                    required
                  />
                </div>

                <InputField
                  label="Adres"
                  value={settings.address}
                  onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                  required
                  multiline
                  rows={2}
                />

                <InputField
                  label="Email"
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  required
                />

                {/* Mistrz kominiarski (protokoły CEEB) — §1/A5, 2026-08-04. Bot wpisuje tę
                    wartość w „osobie uczestniczącej" protokołu; bez niej odmawia startu. */}
                <InputField
                  label="Mistrz kominiarski (protokoły CEEB)"
                  value={settings.protocolMasterName || ''}
                  onChange={(e) => setSettings({ ...settings, protocolMasterName: e.target.value })}
                  placeholder="np. Marcin Szotowski"
                  hint="Wpisywany tam, gdzie poniżej nie podano osoby."
                />

                {/* 060 (Bronek, docx 5.10): osoby uczestniczące per miejsce CEEB + rubryka zaleceń lokali.
                    Przegląd zapamiętuje osoby przy założeniu (migawka, trigger 060), a założony przed wpisaniem
                    ról — przy pierwszej wysyłce do CEEB (D3, 2026-10-10); potem zmiana tutaj go nie przepisuje. */}
                <hr style={hairline} />
                <h3 style={{ ...sectionHeading, fontSize: 'var(--text-body)' }}>Protokół CEEB — osoby uczestniczące</h3>
                <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
                  Puste pole = mistrz kominiarski. Przegląd zapamiętuje te osoby przy założeniu, a przegląd
                  założony przed ich wpisaniem — przy pierwszej wysyłce do CEEB (także wersji roboczej); potem
                  zmiana tutaj już go nie dotyczy. „Osoba uczestnicząca w kontroli” wpisana w przeglądzie ma
                  pierwszeństwo.
                </p>
                <InputField
                  label="Osoba uczestnicząca w inwentaryzacji budynku"
                  value={settings.ceebParticipantBuilding || ''}
                  onChange={(e) => setSettings({ ...settings, ceebParticipantBuilding: e.target.value })}
                  placeholder="np. Marcin Szotowski"
                />
                <InputField
                  label="Osoba uczestnicząca w kontroli (ostatnia strona protokołu)"
                  value={settings.ceebParticipantControl || ''}
                  onChange={(e) => setSettings({ ...settings, ceebParticipantControl: e.target.value })}
                  placeholder="np. Marcin Szotowski"
                />
                <InputField
                  label="Osoba uczestnicząca w inwentaryzacji lokalu"
                  value={settings.ceebParticipantApartment || ''}
                  onChange={(e) => setSettings({ ...settings, ceebParticipantApartment: e.target.value })}
                  placeholder="np. Użytkownik"
                />
                <SelectField
                  label="Zalecenia lokali w protokole CEEB"
                  value={settings.ceebApartmentRecsPlacement || 'abnormalities'}
                  onChange={(e) => setSettings({
                    ...settings,
                    ceebApartmentRecsPlacement: toRecsPlacement(e.target.value),
                  })}
                >
                  <option value="abnormalities">Wszystkie w „Wszelkie wskazane nieprawidłowości…” (dotychczas)</option>
                  <option value="other_deficiencies">Wszystkie w „Inne uchybienia mające pośredni wpływ…”</option>
                  <option value="by_card">Wg kart: bez karty → „Inne uchybienia…”, z podpisaną kartą → informacja o załączniku</option>
                </SelectField>
                {settings.ceebApartmentRecsPlacement === 'other_deficiencies' && (
                  <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
                    Zalecenia każdego lokalu („w m.X - …”) są wpisywane do „Inne uchybienia mające pośredni wpływ na
                    działanie przewodów kominowych”, po zaleceniach budynku. „Wszelkie wskazane nieprawidłowości…”
                    dostaje wyłącznie nieprawidłowości kominowe budynku. Ocena stanu przewodów („kropki”) zależy tylko
                    od nieprawidłowości kominowych budynku — w każdym trybie.
                  </p>
                )}
                {settings.ceebApartmentRecsPlacement === 'by_card' && (
                  <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
                    Lokal z podpisaną kartą jest pomijany w treści protokołu tylko wtedy, gdy bot zobaczy na liście
                    załączników w CEEB plik z aktualnymi kartami — protokół mówi o kartach akapitem „Użytkownicy
                    mieszkań obecni podczas przeprowadzania kontroli…” (A4), a bez niego zdaniem o załączniku.
                    W każdym innym przypadku zalecenia lokalu są wpisywane do „Inne uchybienia mające pośredni wpływ
                    na działanie przewodów kominowych”.
                  </p>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-2)' }}>
                  <Button
                    variant="primary"
                    icon={saveLoading ? undefined : 'check'}
                    onClick={handleSaveCompanySettings}
                    disabled={saveLoading}
                  >
                    {saveLoading ? 'Zapisywanie...' : 'Zapisz zmiany'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab 2: Employees */}
      {tabValue === 1 && (
        <div role="tabpanel" id="settings-tabpanel-1" aria-labelledby="settings-tab-1">
          <div style={card}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 'var(--space-4)',
                flexWrap: 'wrap',
                marginBottom: 'var(--space-4)',
              }}
            >
              <h2 style={sectionHeading}>Pracownicy</h2>
              <Button variant="primary" icon="plus" onClick={handleAddEmployee}>
                Dodaj pracownika
              </Button>
            </div>
            <hr style={hairline} />

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-sans)' }}>
                <thead>
                  <tr style={{ borderBottom: 'var(--border-width-strong) solid var(--border-strong)' }}>
                    {['Imię i nazwisko', 'Email', 'Telefon', 'Rola'].map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left',
                          padding: 'var(--space-3) var(--space-4)',
                          fontSize: 'var(--text-label)',
                          fontWeight: 'var(--weight-semibold)',
                          color: 'var(--text-heading)',
                          whiteSpace: 'nowrap',
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                    <th
                      style={{
                        textAlign: 'right',
                        padding: 'var(--space-3) var(--space-4)',
                        fontSize: 'var(--text-label)',
                        fontWeight: 'var(--weight-semibold)',
                        color: 'var(--text-heading)',
                      }}
                    >
                      Akcje
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((employee) => (
                    <tr
                      key={employee.id}
                      style={{ borderBottom: 'var(--border-width) solid var(--border)', height: 64 }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-hover)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td
                        style={{
                          padding: 'var(--space-4)',
                          verticalAlign: 'middle',
                          minWidth: 160,
                          fontSize: 'var(--text-body)',
                          color: 'var(--text-primary)',
                        }}
                      >
                        {employee.fullName}
                      </td>
                      <td style={{ padding: 'var(--space-4)', minWidth: 200, fontSize: 'var(--text-body)', color: 'var(--text-primary)' }}>
                        {employee.email}
                      </td>
                      <td style={{ padding: 'var(--space-4)', minWidth: 120, fontSize: 'var(--text-body)', color: 'var(--text-primary)' }}>
                        {employee.phone}
                      </td>
                      <td style={{ padding: 'var(--space-4)', minWidth: 100 }}>
                        <StatusChip
                          status={employee.role === 'admin' ? 'rec' : 'todo'}
                          label={employee.role === 'admin' ? 'Administrator' : 'Kominiarz'}
                          size="sm"
                        />
                      </td>
                      <td style={{ padding: 'var(--space-4)', minWidth: 120, whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
                          <Button
                            variant="ghost"
                            icon="pencil"
                            aria-label="Edytuj pracownika"
                            onClick={() => handleEditEmployee(employee)}
                            style={{ padding: '0 var(--space-3)' }}
                          >
                            {''}
                          </Button>
                          <Button
                            variant="ghost"
                            icon="trash"
                            aria-label="Usuń pracownika"
                            onClick={() => setDeleteEmployeeId(employee.id)}
                            style={{ padding: '0 var(--space-3)', color: 'var(--status-deny)' }}
                          >
                            {''}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {employees.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ padding: 'var(--space-8)', textAlign: 'center' }}>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 'var(--space-3)',
                            color: 'var(--text-muted)',
                          }}
                        >
                          <Icon name="users" size={40} />
                          <span style={{ fontSize: 'var(--text-body)' }}>
                            Brak pracowników. Dodaj pierwszego pracownika.
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Tab 3: Announcement Template */}
      {tabValue === 2 && (
        <div role="tabpanel" id="settings-tabpanel-2" aria-labelledby="settings-tab-2">
          <div style={card}>
            <h2 style={sectionHeading}>Szablon ogłoszeń o przeglądach</h2>
            <hr style={hairline} />

            <Banner severity="info">
              Dostępne zmienne: {'{data}'}, {'{adres}'}, {'{kominiarz}'}, {'{telefon}'}, {'{firma}'}
            </Banner>

            <InputField
              label="Treść szablonu"
              multiline
              rows={12}
              value={settings.announcementTemplate}
              onChange={(e) => setSettings({ ...settings, announcementTemplate: e.target.value })}
              placeholder="Wprowadź szablon ogłoszenia..."
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-5)' }}>
              <Button
                variant="primary"
                icon={saveLoading ? undefined : 'check'}
                onClick={handleSaveCompanySettings}
                disabled={saveLoading}
              >
                {saveLoading ? 'Zapisywanie...' : 'Zapisz szablon'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tab 5: Tablety — stan zdrowia urządzeń kominiarzy (2026-10-09, migr. 061) */}
      {tabValue === 4 && (
        <div role="tabpanel" id="settings-tabpanel-4" aria-labelledby="settings-tab-4">
          <div style={card}>
            <h2 style={sectionHeading}>Tablety</h2>
            <hr style={hairline} />
            <DevicesPanel />
          </div>
        </div>
      )}

      {/* Tab 4: CEEB Integration */}
      {tabValue === 3 && (
        <div role="tabpanel" id="settings-tabpanel-3" aria-labelledby="settings-tab-3">
          <div style={card}>
            <h2 style={sectionHeading}>Integracja z systemem CEEB</h2>
            <hr style={hairline} />

            <Banner severity="info">
              Połączenie z CEEB pozwala na automatyczną synchronizację danych przeglądów.
              Sesja będzie automatycznie przedłużana co 50 minut.
            </Banner>

            {/* Connection Status — inline tint box */}
            {ceebConnectionStatus !== 'disconnected' && (
              <div
                style={{
                  marginBottom: 'var(--space-5)',
                  padding: 'var(--space-4)',
                  borderRadius: 'var(--radius-md)',
                  background:
                    ceebConnectionStatus === 'connected'
                      ? 'var(--status-ok-bg)'
                      : ceebConnectionStatus === 'connecting'
                        ? 'var(--blue-100)'
                        : 'var(--status-deny-bg)',
                  borderLeft: `6px solid ${
                    ceebConnectionStatus === 'connected'
                      ? 'var(--status-ok-bar)'
                      : ceebConnectionStatus === 'connecting'
                        ? 'var(--action-primary)'
                        : 'var(--status-deny-bar)'
                  }`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                  <span
                    style={{
                      color:
                        ceebConnectionStatus === 'connected'
                          ? 'var(--status-ok)'
                          : ceebConnectionStatus === 'connecting'
                            ? 'var(--action-primary)'
                            : 'var(--status-deny)',
                      display: 'inline-flex',
                    }}
                  >
                    {ceebConnectionStatus === 'connecting' ? (
                      <Spinner size={22} />
                    ) : (
                      <Icon name={ceebConnectionStatus === 'connected' ? 'check-circle' : 'triangle-alert'} size={22} />
                    )}
                  </span>
                  <span
                    style={{
                      fontSize: 'var(--text-body)',
                      fontWeight: 'var(--weight-semibold)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    Status:{' '}
                    {ceebConnectionStatus === 'connected'
                      ? 'Połączono'
                      : ceebConnectionStatus === 'connecting'
                        ? 'Łączenie...'
                        : 'Błąd połączenia'}
                  </span>
                </div>
                {ceebLastConnected && (
                  <p
                    style={{
                      margin: 'var(--space-2) 0 0 0',
                      fontSize: 'var(--text-caption)',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    Ostatnie połączenie: {formatDate(ceebLastConnected)}
                  </p>
                )}
              </div>
            )}

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 'var(--space-4)',
                marginBottom: 'var(--space-5)',
              }}
            >
              <InputField
                label="Login CEEB"
                value={ceebLogin}
                onChange={(e) => setCeebLogin(e.target.value)}
                disabled={ceebConnectionStatus === 'connecting'}
                required
              />
              <InputField
                label="Hasło CEEB"
                type="password"
                value={ceebPassword}
                onChange={(e) => setCeebPassword(e.target.value)}
                disabled={ceebConnectionStatus === 'connecting'}
                required
              />
            </div>

            <Banner severity="info">
              Proces połączenia może potrwać ~30 sekund. System automatycznie rozwiąże reCAPTCHA i zaloguje się do CEEB.
            </Banner>

            <div>
              <Button
                variant="primary"
                icon={ceebConnectionStatus === 'connecting' ? undefined : 'send'}
                onClick={handleCeebConnect}
                disabled={ceebConnectionStatus === 'connecting' || !ceebLogin || !ceebPassword}
              >
                {ceebConnectionStatus === 'connecting' ? 'Łączenie...' : 'Połącz z CEEB'}
              </Button>
            </div>

            {/* Raport uwag Bronka 2026-10-19 §4 (G4/G3): stan przełączników bota — tylko podgląd, tylko superadmin
                (GET /ceeb/flags odpowiada 403 innym). Zapisu z panelu celowo nie ma. */}
            {isSuperadmin && <CeebFlagsPanel />}
          </div>
        </div>
      )}

      {/* Employee Dialog */}
      <ModalShell
        open={employeeDialogOpen}
        title={editingEmployee ? 'Edytuj pracownika' : 'Dodaj pracownika'}
        width="min(560px, 90vw)"
        onClose={() => setEmployeeDialogOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEmployeeDialogOpen(false)}>
              Anuluj
            </Button>
            <Button variant="primary" onClick={handleSaveEmployee} disabled={saveLoading}>
              {saveLoading ? 'Zapisywanie...' : 'Zapisz'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <InputField
            label="Imię i nazwisko"
            value={employeeForm.fullName}
            onChange={(e) => setEmployeeForm({ ...employeeForm, fullName: e.target.value })}
            required
          />
          <InputField
            label="Email"
            type="email"
            value={employeeForm.email}
            onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
            required
          />
          <InputField
            label="Telefon"
            value={employeeForm.phone}
            onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value })}
            required
          />
          <InputField
            label="Hasło"
            type="password"
            value={employeeForm.password}
            onChange={(e) => setEmployeeForm({ ...employeeForm, password: e.target.value })}
            required={!editingEmployee}
            hint={editingEmployee ? 'Pozostaw puste, jeśli nie chcesz zmieniać hasła' : undefined}
          />
          <SelectField
            label="Rola"
            value={employeeForm.role}
            onChange={(e) => setEmployeeForm({ ...employeeForm, role: e.target.value as 'admin' | 'sweep' })}
          >
            <option value="sweep">Kominiarz</option>
            <option value="admin">Administrator</option>
          </SelectField>
        </div>
      </ModalShell>

      {/* Employee delete confirmation (replaces window.confirm) */}
      <ConfirmModal
        open={deleteEmployeeId !== null}
        title="Usuń pracownika"
        message="Czy na pewno chcesz usunąć tego pracownika?"
        confirmLabel="Usuń"
        cancelLabel="Anuluj"
        destructive
        onCancel={() => setDeleteEmployeeId(null)}
        onConfirm={() => {
          if (deleteEmployeeId) {
            handleDeleteEmployee(deleteEmployeeId);
          }
          setDeleteEmployeeId(null);
        }}
      />
    </div>
  );
};

export default SettingsTab;
